import { Placement } from "@popperjs/core";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useOutsideClick } from "../../hooks/useOutsideClick";
import Position from "../layout/Position";

const TranslationWrapper = styled.div<any>`
  width: auto;
  z-index: 999;
`;

export interface ButtonMenuProps {
  placement?: Placement;
  button: React.ReactElement;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  buttonRef?: (el: HTMLElement) => void;
  show?: boolean;
  menu?: any;

  /** Override: Positions the menu about a different element than
   * props.children. Used in SplitButton.*/
  refElement?: HTMLElement | null;
}

export type MenuDispatchMethods = {
  show: () => void;
  hide: () => void;
};

/**
 * Button Menu
 *
 * Consists of a `Button` and a `Menu`, the latter of which is revealed when the
 * `Button` is clicked.
 *
 * @param props
 */
const ButtonMenu: React.FC<React.PropsWithChildren<ButtonMenuProps>> = (
  props,
) => {
  const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);
  const [buttonElement, setButtonElement] = useState<HTMLElement | null>(null);

  // swallow referential instability for props
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buttonRef = useMemo(() => props.buttonRef, []);

  useOutsideClick(menuElement, (e: MouseEvent) => {
    if (menuElement && buttonElement) {
      if (buttonElement?.contains(e.target as Node)) {
        return;
      }
      props.onExpandedChange?.(!props.expanded);
    }
  });

  useEffect(() => {
    buttonRef?.(buttonElement as HTMLElement);
  }, [buttonElement, buttonRef]);

  const button = React.cloneElement(props.button, {
    isMenuShowing: props.expanded,
    ref: setButtonElement,
    onClick: () => {
      props.onExpandedChange?.(!props.expanded);
    },
  });

  return (
    <Position
      placement={props.placement}
      refElement={props.refElement}
      refElementRef={setButtonElement}
      popperElementRef={setMenuElement}
      popper={
        props.expanded ? (
          <TranslationWrapper>{props.children}</TranslationWrapper>
        ) : undefined
      }
    >
      {button}
    </Position>
  );
};

export default ButtonMenu;
